import { ActionIcon, Divider, Group, Paper, Stack, Text, Textarea } from "@mantine/core";
import { useField } from "@mantine/form";
import { IconSend2 } from "@tabler/icons-react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import { httpPostGraphql } from "shared/api/httpClient";
import { useMessageActions } from "shared/components/message/useMessageActions";
import { usePermissionCheck } from "shared/stores/oidc";
import { fail, to } from "shared/utils/fns";
import { maybe } from "shared/utils/maybe";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import { Language, Permission } from "src/gql/graphql";

export const MessageNotes = (props: { msgId: string; language?: Language; mode?: "form" | "widget" }) => {
    const actions = useMessageActions();
    const { data } = useSuspenseQuery(query(props.msgId));
    const content = useField({
        initialValue: "",
    });

    const hasCreate =
        usePermissionCheck(Permission.GroupMessageSendFrom, data.sender.group.id ?? fail("expected sender to exist.")) &&
        props.mode != "widget";
    const hasDivider = hasCreate || data.notes.length > 0;

    return (
        <>
            {hasDivider && <Divider my={0} />}
            <Stack gap="xs">
                {hasCreate && (
                    <Group align="flex-start">
                        <Textarea
                            autosize
                            minRows={3}
                            placeholder="Add a note..."
                            aria-label="Add a note"
                            styles={{ input: { padding: "8px 12px" } }}
                            style={{ flexGrow: 1, position: "relative" }}
                            {...content.getInputProps({ withError: false })}
                            rightSection={
                                <ActionIcon
                                    disabled={!content.isDirty()}
                                    variant="subtle"
                                    color="gray"
                                    style={{ marginTop: "auto", marginBottom: "4px" }}
                                    onClick={() =>
                                        void actions
                                            .openCreateNoteModal({ id: props.msgId, content: content.getValue() })
                                            .then(() => content.setValue(""))
                                            .catch(notify.catch)
                                    }
                                    aria-label="Send note"
                                >
                                    <IconSend2 size={16} />
                                </ActionIcon>
                            }
                        ></Textarea>
                    </Group>
                )}

                {data.notes.map((note) => {
                    const noteAuthorLabelId = `note-${note.id}-author`;
                    const noteTimestampLabelId = `note-${note.id}-timestamp`;

                    return (
                        <motion.div layout key={note.id + props.language}>
                            <Paper withBorder p="xs">
                                <Group justify="space-between" wrap="nowrap" align="flex-start">
                                    <Textarea
                                        variant="unstyled"
                                        autosize
                                        readOnly
                                        style={{ flexGrow: 1 }}
                                        aria-labelledby={`${noteAuthorLabelId} ${noteTimestampLabelId}`}
                                    >
                                        {note.locales.find((it) => it.language == props.language)?.body ??
                                            note.locales.find((it) => it.language == Language.English)?.body ??
                                            note.locales.at(0)?.body}
                                    </Textarea>
                                    <Stack gap={0}>
                                        <Text id={noteAuthorLabelId} size="xs" c="dimmed" ta="right">
                                            {maybe(note.publishedBy)?.take((it) => `${it.name} (${it.loginEmail})`) ?? "System"}
                                        </Text>
                                        <Text id={noteTimestampLabelId} size="xs" c="dimmed" ta="right">
                                            {new Date(+note.publishedAt!).toLocaleString()}
                                        </Text>
                                    </Stack>
                                </Group>
                            </Paper>
                        </motion.div>
                    );
                })}
            </Stack>
        </>
    );
};

const query = (id: string) =>
    queryOptions({
        queryKey: qk("messages", "notes", id),
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query MessageNotes($id: UUID!) {
                        message(id: $id) {
                            id
                            sender {
                                group {
                                    id
                                }
                            }
                            notes {
                                id
                                locales {
                                    language
                                    body
                                }
                                publishedAt
                                publishedBy {
                                    name
                                    loginEmail
                                }
                            }
                        }
                    }
                `),
                { id },
            ),
        select: to("message"),
    });
